/**
 * Qualidade do dado (Administração) — verificações que apontam onde o dado do
 * ERP, ou a cópia dele no AURA, está incompleto ou atípico. Cada cartão diz de
 * quem é a correção (ERP ou AURA), o número, a evolução e exemplos para
 * conferir; o "i" explica de onde vem, como ler e o que o número não diz.
 */
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ShieldCheck, RefreshCw, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import InfoTip from '../components/InfoTip';

const COR = {
  ok: { bg: '#d1f2e0', fg: '#11643b', rot: 'Em ordem' },
  atencao: { bg: '#fff4cf', fg: '#7a5a00', rot: 'Atenção' },
  problema: { bg: '#fbd5d5', fg: '#8e1b1b', rot: 'Corrigir' },
};
const RESP = {
  ERP: 'Correção no sistema da operadora (cadastro ou lançamento da conta)',
  AURA: 'Correção no AURA (cópia do dado ou uso dele nas telas)',
};
const CARD = { background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', padding: 18, marginBottom: 12 };
const TH = { textAlign: 'left', padding: '6px 8px', fontSize: 12, color: '#7f8c8d', borderBottom: '1px solid #e8e8e8', fontWeight: 600 };
const TD = { padding: '5px 8px', fontSize: 12, color: '#2c3e50', borderBottom: '1px solid #f0f0f0' };
const ROTULO = {
  matricula: 'Matrícula', nome: 'Nome', ultima_conta: 'Última conta', valor: 'Valor (R$)', plano: 'Plano',
  parentesco: 'Parentesco', cd_prestador: 'Cód. prestador', prestador: 'Prestador', rede: 'Rede',
  consultas: 'Consultas', especialidade: 'Especialidade', cbo: 'CBO', codigo: 'Código',
  linhas_12m: 'Linhas (12 meses)', procedimentos_mais_comuns: 'Procedimentos mais comuns (qtd.)',
};
const num = (v) => (typeof v === 'number' ? v.toLocaleString('pt-BR') : v ?? '—');

function baixarCsv(item) {
  const cols = item.amostra.colunas;
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const linhas = item.amostra.linhas.map((l) => cols.map((c) => esc(l[c])).join(';'));
  const blob = new Blob(['﻿' + [cols.map((c) => esc(ROTULO[c] || c)).join(';'), ...linhas].join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `qualidade_${item.codigo}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function Verificacao({ item }) {
  const [aberto, setAberto] = useState(false);
  const c = COR[item.situacao] || { bg: '#f3f4f6', fg: '#7f8c8d', rot: 'Indisponível' };
  const temAmostra = item.amostra && item.amostra.linhas.length > 0;
  return (
    <div style={CARD}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ background: c.bg, color: c.fg, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>{c.rot}</span>
            <b style={{ fontSize: 15, color: '#2c3e50' }}>{item.titulo}</b>
            <InfoTip w={380} texto={item.ajuda} />
          </div>
          <div style={{ fontSize: 13, color: '#7f8c8d', marginTop: 4 }}>{item.pergunta}</div>
          <div style={{ fontSize: 12, color: '#7f8c8d', marginTop: 4 }}>
            <span style={{ border: '1px solid #dce1e4', borderRadius: 6, padding: '1px 6px', marginRight: 6, fontWeight: 600 }}>{item.responsavel}</span>
            {RESP[item.responsavel]}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {item.disponivel ? (
            <>
              <div style={{ fontSize: 26, fontWeight: 700, color: c.fg, fontFamily: "'SF Mono', Consolas, monospace" }}>{num(item.valor)}</div>
              <div style={{ fontSize: 12, color: '#7f8c8d' }}>{item.unidade}</div>
            </>
          ) : <div style={{ fontSize: 12, color: '#7f8c8d', maxWidth: 260 }}>{item.motivo}</div>}
        </div>
      </div>

      {item.serie && item.serie.length > 0 && (
        <div style={{ height: 110, marginTop: 10 }}>
          <ResponsiveContainer>
            <BarChart data={item.serie} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} width={42} allowDecimals={false} />
              <Tooltip formatter={(v, n, p) => [`${num(v)} de ${num(p.payload.base)}`, 'Encontrado']} />
              <Bar dataKey="valor" fill={c.fg} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {temAmostra && (
        <div style={{ marginTop: 8 }}>
          <button onClick={() => setAberto(!aberto)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#3498db', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0 }}>
            {aberto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Exemplos para conferir ({item.amostra.linhas.length}{item.amostra.linhas.length >= 50 ? ' maiores' : ''})
          </button>
          {aberto && (
            <div style={{ marginTop: 6 }}>
              <div style={{ textAlign: 'right', marginBottom: 4 }}>
                <button onClick={() => baixarCsv(item)} style={{ border: '1px solid #e8e8e8', background: '#fff', borderRadius: 8, padding: '4px 10px', fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Download size={12} /> Baixar CSV
                </button>
              </div>
              <div style={{ maxHeight: 320, overflow: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead><tr>{item.amostra.colunas.map((col) => <th key={col} style={TH}>{ROTULO[col] || col}</th>)}</tr></thead>
                  <tbody>
                    {item.amostra.linhas.map((l, i) => (
                      <tr key={i}>{item.amostra.colunas.map((col) => <td key={col} style={TD}>{num(l[col])}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminQualidadeDado() {
  const [d, setD] = useState(null);
  const [falhou, setFalhou] = useState(false);

  // O cálculo roda em segundo plano no servidor (até 2 min): a resposta traz o
  // último resultado e `calculando`; enquanto calcula, pergunta de novo.
  const carregar = (atualizar = false) => {
    api.get('/qualidade-dado', { params: { atualizar } })
      .then((r) => { setD(r.data); setFalhou(false); })
      .catch(() => { setFalhou(true); toast.error('Falha ao consultar as verificações'); });
  };
  useEffect(() => { carregar(); }, []);
  useEffect(() => {
    if (!d?.calculando) return undefined;
    const t = setTimeout(() => carregar(), 5000);
    return () => clearTimeout(t);
  }, [d]);
  const carregando = !!d?.calculando;
  const temItens = !!(d && d.itens && d.itens.length > 0);

  const resumo = temItens ? ['problema', 'atencao', 'ok'].map((s) => ({ s, n: d.itens.filter((i) => i.situacao === s).length })) : [];

  return (
    <div style={{ padding: 20, background: '#f5f7fa', minHeight: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <ShieldCheck size={22} color="var(--brand, #20a490)" />
        <h2 style={{ margin: 0, color: '#2c3e50' }}>Qualidade do dado</h2>
        <InfoTip w={380} texto="Verificações automáticas do dado que vem do sistema da operadora (ERP) e da cópia dele no AURA. Cada uma diz o que confere, quanto encontrou, como evoluiu e mostra exemplos para a equipe responsável avaliar. Não altera nada: só aponta. Recalcula a cada hora, ou no botão ao lado." />
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={() => carregar(true)} disabled={carregando} style={{ border: '1px solid #e8e8e8', background: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: carregando ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={13} className={carregando ? 'spin' : ''} /> Recalcular
          </button>
        </div>
      </div>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#7f8c8d' }}>
        Contas médicas dos últimos 12 meses e cadastro de beneficiários.
        {d?.calculado_em && <> Calculado em {d.calculado_em}. {resumo.map(({ s, n }) => n > 0 && <span key={s} style={{ marginLeft: 8, color: COR[s].fg, fontWeight: 600 }}>{n} {COR[s].rot.toLowerCase()}</span>)}</>}
        {carregando && temItens && <span style={{ marginLeft: 8 }}>Recalculando…</span>}
      </p>
      {d?.erro && <div style={{ ...CARD, borderColor: '#f39c12', fontSize: 13 }}>O último cálculo falhou: {d.erro}</div>}
      {!temItens
        ? <div style={CARD}><span style={{ color: '#7f8c8d', fontSize: 13 }}>{falhou ? 'Não foi possível consultar as verificações.' : 'Calculando as verificações… leva de 30 segundos a 2 minutos. A tela atualiza sozinha.'}</span></div>
        : <div style={{ opacity: carregando ? 0.6 : 1 }}>{d.itens.map((i) => <Verificacao key={i.codigo} item={i} />)}</div>}
    </div>
  );
}
